import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { useForm, useWatch } from "react-hook-form";
import { toast } from "react-toastify";
import { ReactSelectField } from "../../components/reactHookFormFields/selectField";
import { TextAreaField } from "../../components/reactHookFormFields/textAreaFields";
import { InputField } from "../../components/reactHookFormFields/inputField";
import { useGroupChatContext } from "../../context/groupchatContext";
import { appointmentApi, useAddAppointmentsMutation, useGetAppointmentByIdQuery, useGetAppointmentOptionsQuery, useGetAppointmentsMetaQuery, useAddProviderAppointmentsMutation } from "../../services/appointmentService";
import { PageDialogContent, PageDialogFooter } from "../../components/dialog";
import { BtnComponent } from "../../components/button";
import SpeechMic from "../../components/mic";
import { DatePickerFeild } from "../../components/reactHookFormFields/datePicker";
import { CheckBox } from "../../components/reactHookFormFields/checkbox";
import { cloneDeep } from "lodash";
import { useGetPatientContactQuery, useGetVezoRoomsQuery, useSaveSessionMutation } from "../../services/vezoService";
import { useGetPracticeAllLocationsQuery, useSendMessageMutation } from "../../services/chatService";
import { ExternalGuest } from "../../components/externalGuests";
import { PatientContact } from "./patientContact";
import baseApiHelper from "../../services/baseApiHelper";
import ActionDialog from "../../components/actionDialog";

export const AppointmentsFollowUp = ({apptId, closeApptPopup}) => {
    
    const groupChatContext = useGroupChatContext();
    const { authData, selectedChat } = useMemo(() => ({
        authData: groupChatContext.authData,
        selectedChat: groupChatContext.selectedChat,
    }));

    const {
        ...formHandler
    } = useForm();

    const {handleSubmit,
        control,
        setValue,
        getValues,
        reset,
        formState: { errors }
    } = formHandler

    const [selectedSlot, setSelectedSlot] = useState("");
    const [displaySlots, setDisplaySlots] = useState([]);
    const [prevAppt, setPrevAppt] = useState({
        provider: null,
        slot: null,
    });
    const [exteralGuests, setExteralGuests] = useState([]);
    const [vezoSessionContacts, setVezoSessionContacts] = useState([]);
    const [apptMetaData, setAppMetaData] = useState(null)
    const [startDate, setStartDate] = useState(new Date());
    const [dateValidate, setDateValidate] = useState(true);
    const [isSlotsLoading, setIsSlotsLoading] = useState(false);
    const [isTeleconsultDisable, setIsTeleconsultDisable] = useState(false)
    const [apptDetails, setApptDetails] = useState(null);
    const [apptAlreadyCreated, setApptAlreadyCreated] = useState({
        isCreated: false,
        apptData: null,
        msg: ""
    });
    const followUpComments = [
        {
        id: "add_follow_up_comments",
        name: "Add Follow up comments",
        label: "Add Follow up comments",
        },
        {
        id: "today",
        name: "Add Follow up",
        label: "Add Follow up",
        },
        {
        id: "day",
        name: "1 Day",
        label: "1 Day",
        },
        {
        id: "week",
        name: "1 Week",
        label: "1 Week",
        },
        {
        id: "month",
        name: "1 Month",
        label: "1 Month",
        },
    ];

    const selectedPid = selectedChat?.pid || apptDetails?.pid || apptDetails?.patient_id || apptDetails?.patient_mrn || apptDetails?.patient?.pid || apptDetails?.patient?.id || apptDetails?.patient?.mrn;

    const currentFollowUp = followUpComments[1].id;
    const isTeleconsultValue = useWatch({ control, name: "is_teleconsult" });
    const currentProviderValue = useWatch({ control, name: "provider" });
    const locationVal = useWatch({ control, name: 'location' }) || {};
    const vezoVisitKeyValue = useWatch({control, name: 'vezo_visit_key'})

    const {data: apptMeta} = useGetAppointmentsMetaQuery({pid: selectedPid}, {skip: !selectedPid})
    const [addAppointment, {isLoading: isSavingAppts}] = useAddAppointmentsMutation();
    const [addProviderAppointment, {isLoading: isSavingProviderAppt}] = useAddProviderAppointmentsMutation();
    const [saveSession, {isLoading: isSavingSession}] = useSaveSessionMutation();
    const [getAppointmentsSlots] = appointmentApi.endpoints.getAppointmentsSlots.useLazyQuery();
    const [getVezoSession] = appointmentApi.endpoints.getVezoSession.useLazyQuery();
    const [sendMessage] = useSendMessageMutation();

    const {data: patientContact} = useGetPatientContactQuery({pid: selectedPid}, {skip: !authData?.is_provider || !selectedPid })
    const {data: vezoAllRooms } = useGetVezoRoomsQuery({user_id: currentProviderValue?.id}, { skip: !authData?.is_provider, refetchOnMountOrArgChange: true });
    const {data: apptOptions} = useGetAppointmentOptionsQuery(null, {skip: !authData?.is_provider});
    const [getAppointmentById] = appointmentApi.endpoints.getAppointmentById.useLazyQuery();
    const {data: practiceAllLocations } = useGetPracticeAllLocationsQuery(null, {skip: !authData?.is_provider})
 
    useEffect(()=>{
        if(apptId && authData?.is_provider){
            getAppointmentById({
                event_id: apptId
            }).then((res) => {
                if(res?.data){
                    setApptDetails(res?.data);
                }
            });
        }
    }, [apptId, authData?.is_provider, getAppointmentById])

    useEffect(()=>{
        if(apptMeta){
            const roles = apptMeta?.roles?.map((v) => ({...v, label: v.title}))
            let provider_list = apptMeta?.provider_list?.map((v) => ({...v, label: v?.name}))
            provider_list.unshift({id: "0", label: "Unassigned", name: "Unassigned"})
            const visit_types = apptMeta?.visit_types?.map((v) => ({...v, label: v.type}))
            setAppMetaData({...apptMeta, roles, provider_list, visit_types })
            if(!apptId){
                const foundVisit = visit_types.find((item)=> item.slug == 'nursing_home_visit_Ethizo');
                setValue('encounter_type', foundVisit)
            }
        }
    }, [apptMeta])

    useEffect(()=>{
        if (selectedChat?.location?.id || selectedChat?.location?.name) {
            setValue('location', {
                id: selectedChat?.location?.id,
                name: selectedChat?.location?.name,
                label: selectedChat?.location?.name,
            });
        }
    }, [selectedChat?.location, setValue])

    useEffect(()=>{
        if (apptMetaData && apptMetaData?.provider_list && apptMetaData?.provider_list?.length > 0) {
            const foundProvider = apptMetaData?.provider_list.find((item) => item.id == authData?.id);
            setValue("provider", foundProvider);
            setValue("status", {
                id: "schedule",
                label: "Schedule",
            })
        }

        if(apptDetails && apptMetaData){
            let data = cloneDeep(apptDetails);
            data.visit_reason = apptDetails.visit_type;
            data.is_teleconsult = apptDetails.conf_call == "yes";
            setIsTeleconsultDisable(data.is_teleconsult)
            data.status = {label: apptDetails.status}
            const provider = apptDetails.providers?.[0];
            const foundProvider = apptMetaData?.provider_list.find((item) => item.id == provider?.id)
            if(foundProvider){
                data.provider = foundProvider;
                setPrevAppt({
                    provider: foundProvider,
                    slot: moment(apptDetails.start_time).format('HH:mm')
                });
            }

            const foundRole = apptMetaData?.roles.find((role) => (role.id == apptDetails.provider_role || role.title == apptDetails.provider_role))
            if(foundRole){
                data.role = foundRole;
            }
            
            const foundVisitType = apptMetaData?.visit_types.find((visitType) => [visitType.id, visitType.slug].includes(apptDetails.encounter_type))
            if(foundVisitType){
                data.encounter_type = foundVisitType;
            }
            if (apptDetails.start_time) {
                setStartDate(new Date(apptDetails.start_time));
                setTimeout(()=> setSelectedSlot(moment(apptDetails.start_time).format('HH:mm')), 500);
            }
            if(data.location_id){
                if(authData?.is_provider){
                    const foundLocation = practiceAllLocations?.find((loc)=>{
                        return loc.id == data.location_id
                    })
                    data.location = foundLocation ? {...foundLocation, label: foundLocation?.name} : {
                        id: data.location_id,
                        name: data.location_name,
                        label: data.location_name,
                    }
                }
                else{
                    data.location = data.location_name;
                }
            }
            
            reset(data)
        }
    }, [apptDetails, apptMetaData, authData?.id, authData?.is_provider, practiceAllLocations, reset, setValue])

    useEffect(() => {
        if (currentProviderValue?.id !== prevAppt?.provider?.id) {
            setSelectedSlot(null);
        } else {
            setSelectedSlot(prevAppt?.slot);
        }
    }, [currentProviderValue])

    useEffect(()=>{
        if(vezoAllRooms?.length && vezoVisitKeyValue){
            getVezoSession({visit_key: vezoVisitKeyValue}).then((vezoSession)=>{
                if(vezoSession?.data?.vezo_id){
                    const foundRoom = vezoAllRooms.find((room)=> room.vezo_id == vezoSession?.data?.vezo_id);
                    if(foundRoom){
                        setValue('vezo_room', {...foundRoom, label: foundRoom.room_name})
                    }
                }
                if(vezoSession?.data?.participants){
                    setExteralGuests(vezoSession?.data?.participants)
                }
                if(vezoSession?.data?.patient_contacts){
                    setVezoSessionContacts(vezoSession?.data?.patient_contacts)
                }
            })
        }
    }, [vezoAllRooms, vezoVisitKeyValue])

    useEffect(() => {
        if (startDate && currentProviderValue && currentProviderValue.id !== "0") {
            setIsSlotsLoading(true);
            getAppointmentsSlots({
                pid: selectedPid,
                location_id: locationVal?.id,
                provider: currentProviderValue.id,
                start_date: moment(startDate).format("YYYY-MM-DD"),
            }).then((res) => {
                if (res?.data){
                    setDisplaySlots(null);
                    setDisplaySlots(res?.data?.slots);
                } else {
                    setDisplaySlots(null);
                }
                setIsSlotsLoading(false);
            });
        }
    }, [startDate, currentProviderValue, locationVal?.id]);

    useEffect(() => {
        if(currentProviderValue && currentProviderValue?.id === "0"){
            setDisplaySlots(null);
            var timeStops = getTimeSlots("06:00", "21:45");
            setDisplaySlots(timeStops?.slots);
        }
    }, [currentProviderValue]);

    const calcualteFollowUpDay = () => {
        let date = "";
        if (currentFollowUp === "today") {
            date = new Date();
        } else if (currentFollowUp === "day") {
            date = new Date(moment().add(1, "days"));
        } else if (currentFollowUp === "week") {
            date = new Date(moment().add(1, "weeks"));
        } else if (currentFollowUp === "month") {
            date = new Date(moment().add(1, "months"));
        } else {
            date = new Date();
        }
        setStartDate(date);
    };

    useEffect(() => {
        calcualteFollowUpDay();
        setDisplaySlots(null);
    }, []);

    useEffect(() => {
        if (startDate) {
            setSelectedSlot && setSelectedSlot(null);
        }
    }, [startDate]);

    const handleClosePopup = () => {
        reset()
        closeApptPopup()
    }

    const onSubmitForm = async(data, e) => {
        e.preventDefault();
        if(!selectedSlot){
            toast.error("Please select a slot");
        }
        else if (!dateValidate) {
            toast.error("Cannot select a previous date");
        }
        else {

            let tempStartDate = startDate;
            let tempHours = selectedSlot.split(":");
            tempStartDate.setHours(tempHours[0], tempHours[1], 0, 0);
            let postData =  {
                pid: selectedPid,
                user_id: data?.provider?.id ? data?.provider?.id : "",
                status: data.status ? data.status.label : "Pending",
                notify_user: 0,
                location_id: data?.location?.id,
                encounter_type: data?.encounter_type?.slug,
                efax_id: "",
                guest_phone: "",
                external_guest: "",
                comments: data?.comments,
                appointment_type_id: "1",
                date: moment(startDate).format("YYYY-MM-DD"),
                startdate: moment(tempStartDate).format("YYYY-MM-DD HH:mm:ss"),
                start_time: moment(tempStartDate).format("YYYY-MM-DD HH:mm:ss"),
                end_time: moment(tempStartDate).add(15, "minutes").format("YYYY-MM-DD HH:mm:ss"),
                duration: "15",
                repeat_unit: "days",
                repeat_until: "num_of_appointments",
                visit_type: data?.visit_reason,
                visit_type_title: data?.visit_reason,
                title: data?.visit_reason,
                provider_role: data?.role?.label || data?.role?.title || data?.role?.id,
                provider_id: data?.provider?.id,
                provider_name: data?.provider?.label,
            }
            
            if(isTeleconsultValue){
                postData.conf_call = 'yes';
                postData.conf_call_value = 'yes';
                if(data.vezo_room){
                    postData.vezo_room_id = data.vezo_room.id;
                }
                postData.guest_email = data.guest_email;
                postData.exteranlParticipants = exteralGuests;

                if(data.patientContact){
                    const tempPatContacts = patientContact.map((item, index)=>{
                        const contatct = data.patientContact[index];
                        return {...item, ...contatct}
                    })
                    postData.pContacts = tempPatContacts;
                }
            }
            
            if(apptDetails?.id){
                postData.id = apptDetails.id
            }
            
            let ApiFun = addAppointment;
            if(authData?.is_provider){
                ApiFun = addProviderAppointment;
            }

            if (apptAlreadyCreated?.isCreated) {
                postData.add_anyway_apt = true
            }

            postData["provider_visit_notes"] = "";
            
            const res = await ApiFun({postData});
            const response = res?.data?.response;
            if(response?.data && response?.data?.id){
                if(isTeleconsultValue && authData?.is_provider){
                    const sessionPayload = {
                        appt_id: response.data?.id,
                        encounter_type: postData.encounter_type,
                        encounter_type_temp: postData.encounter_type,
                        duration: postData.duration,
                        datetime: postData.start_time,
                        title: `${selectedChat?.patient?.fname || ""} ${selectedChat?.patient?.lname || ""} ${moment().format('MM/DD/YYYY hh:mm A')}`.trim(),
                        title_changed: "patientSeleted",
                        user_id: postData.user_id,
                        user_name: postData.provider_name,
                        vezo_id: data.vezo_room?.vezo_id,
                        pid: selectedPid,
                        participants: exteralGuests,
                        patient_contacts: postData.pContacts,
                        timezone_long: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    }
        
                    const sessionResponse = await saveSession(sessionPayload);
                    if(sessionResponse.data){
                        const payload = {pid: selectedPid, message:{type: 'event', event_type: 'appointment', data: {start_time: postData.start_time, id: response.data.id, provider_name: postData.provider_name, vezo_id: sessionResponse.data.vezo_id, visit_url: sessionResponse.data.visit_url, visit_key: sessionResponse.data.visit_key}}};
                        sendMessage(payload);
                    }
                }
                else if(authData?.is_provider){
                    const payload = {pid: selectedPid, message:{type: 'event', event_type: 'request_appointment', data: {start_date: postData.startdate, status: postData.status, id: response.data.id}}};
                    sendMessage(payload);
                }
                
                toast.success(res?.data?.response?.msg);
                handleClosePopup();
                baseApiHelper().invalidateTags(["PatientAppointments"]);
                setApptAlreadyCreated({
                    isCreated: false,
                    apptData: null,
                    msg: ""
                });
            }
            else {
                // if(response?.code === -1){
                //     toast.error(response?.msg);
                // }
                if(response?.code === -15 || response?.code === "-15"){
                    setApptAlreadyCreated({
                        isCreated: true,
                        apptData: data,
                        msg: response?.msg
                    })
                }
                // else {
                //     toast.error("Appointment already exist!");
                // }
            }
           
        }
    };

    function getTimeSlots(start, end) {
        let calendar = {
            book_per_slot: "1",
            slot_time: "15",
            slot_time_formatted: "15min",
            slots: [],
        };
        var startTime = moment(start, "HH:mm");
        var endTime = moment(end, "HH:mm");
        if (endTime.isBefore(startTime)) {
            endTime.add(1, "day");
        }

        while (startTime <= endTime) {
            let formatTime = new moment(startTime).format("HH:mm").split(":");
            let m = moment(startDate).set({
                h: formatTime[0],
                m: formatTime[1],
                s: "00",
            });

            calendar.slots.push({
                start_time: new moment(startTime).format("HH:mm"),
                end_time: moment(moment(startTime, "HH:mm").add(15, "minutes")).format(
                "HH:mm"
                ),
                datetime: moment(m).format("YYYY-MM-DD HH:mm:ss"),
                block: false,
                block_reason: "",
                appointments: [],
                calender_type_id: "Professional",
                calender_id: "269",
                notify_user: "0",
                book_per_slot: "1",
                slot_time: "15",
                slot_time_formatted: "15min",
            });
            startTime.add(15, "minutes");
        }
        return calendar;
    }

    const compareDates = (currentDate) => {
        const date1 = new Date(currentDate);
        const date2 = new Date();

        if (date1 <= date2.setDate(date2.getDate() - 1)) {
            setDateValidate(false);
        } else {
            setDateValidate(true);
            setStartDate(currentDate);
        }
    };

    const handleSelectedSlot = (slot, pecentage) => {
        if (pecentage < 100) {
            setSelectedSlot && setSelectedSlot(slot);
        } 
        else {
            toast.error(
                "Appointments booking per slots limit has exceeded. Please contact Practice Admin or change Booking per slot in Calendar."
            );
        }
    };

    const onSpeechAvailable = (text) =>{
        const commentsVal = getValues('comments');
        const finalText = ((commentsVal ? commentsVal : '')+' '+text).trim()
        setValue('comments', finalText)
    }


    return (
        <>
            {apptAlreadyCreated?.isCreated && (
                <ActionDialog
                  onCancelHandler={() => {
                    setApptAlreadyCreated({
                      isCreated: false,
                      apptData: null,
                      msg: ""
                    });
                  }}
                  onConfirmHandler={() => {
                    onSubmitForm(apptAlreadyCreated?.apptData, {preventDefault: ()=>{}})
                    setApptAlreadyCreated({
                      isCreated: false,
                      apptData: null,
                      msg: ""
                    });
                  }}
                  showModal={apptAlreadyCreated?.isCreated}
                  headerTitle="Caution!"
                  title={apptAlreadyCreated?.msg ||'This patient has already an appointment at "Location Name" on "Date Time". Do you still want to create another appointment'}
                  cancelButtonText="No"
                  confirmButtonText="Yes"
                  setShowModal={() => {
                    setApptAlreadyCreated({
                      isCreated: false,
                      apptData: null,
                      msg: ""
                    });
                  }}
                />
              )}
            <PageDialogContent contentCls="p-3 sm:p-4 md:px-6">
                <form id="save_appointment_form" onSubmit={handleSubmit(onSubmitForm)} className="min-w-0">
                    <div className="items-center min-w-0">
                        <div className="grid gap-3 sm:gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
                            <div className="min-w-0">
                                <ReactSelectField
                                    title="Provider"
                                    name="provider"
                                    control={control}
                                    optionData={apptMetaData?.provider_list}
                                    menuPlacement={"bottom"}
                                />
                            </div>
                            <div className="min-w-0">
                                <ReactSelectField
                                    title="Role"
                                    name={`role`}
                                    control={control}
                                    errors={currentProviderValue?.id !== "0" ? null : errors}
                                    rules={{
                                        required: {
                                            value: currentProviderValue?.id !== "0" ? false : true,
                                        },
                                    }}
                                    optionData={apptMetaData?.roles}
                                    menuPlacement={"bottom"}
                                />
                            </div>
                            <div className="min-w-0">
                                {authData?.is_provider ? 
                                    <ReactSelectField
                                        title="Location"
                                        name="location"
                                        control={control}
                                        optionData={practiceAllLocations?.map((loc) => ({
                                            label: loc.name,
                                            ...loc
                                        }))}
                                        menuPlacement={"bottom"}
                                    />
                                    :
                                    <InputField
                                        name="location.name"
                                        isDisabled={true}
                                        control={control}
                                    />
                                }
                            </div>
                            <div className="min-w-0">
                                <ReactSelectField
                                    title="Visit Type"
                                    name="encounter_type"
                                    control={control}
                                    optionData={apptMetaData?.visit_types}
                                    menuPlacement={"bottom"}
                                />
                            </div>
                            <div className="min-w-0">
                                <InputField
                                    title="Visit Reason"
                                    name={`visit_reason`}
                                    control={control}
                                />
                            </div>
                            {authData?.is_provider ?
                                <div className="min-w-0 sm:col-span-2 xl:col-span-1">
                                    <ReactSelectField
                                        title="Status"
                                        name="status"
                                        control={control}
                                        optionData={apptOptions?.statuses?.map((v) => ({
                                            label: v,
                                        }))}
                                        menuPlacement={"bottom"}
                                        errors={errors}
                                        rules={{
                                            required: {
                                                value: true,
                                            },
                                        }}
                                    />
                                </div>
                            : null}
                        </div>
                        <div className="items-center pt-5 min-w-0">
                            <div className="flex flex-col lg:flex-row gap-4 lg:gap-x-3">
                                <div className="items-center flex-shrink-0">
                                    <div className="followUp-datePicker">
                                        <DatePickerFeild
                                            control={control}
                                            name="start_date"
                                            dateTime={false}
                                            selected={startDate}
                                            onChange={(date) => compareDates(date)}
                                            minDate={moment().toDate()}
                                            inline
                                        />
                                    </div>
                                </div>
                                <div className="items-center w-full min-w-0 flex-1">
                                    <div className="bg-white whiteShadows bg-opacity-50 min-h-240">
                                        <div className="p-3 min-h-[280px] md:min-h-[365px]">
                                            <div className="grid gap-x-3 gap-y-4 grid-cols-1 md:grid-cols-12">
                                                <div className="md:col-span-6 min-w-0">
                                                    <div className="block text-center items-center">
                                                        <p className="text-c-black-600 font-semibold text-base">
                                                            AM
                                                        </p>
                                                    </div>
                                                    {isSlotsLoading && currentProviderValue?.id !== "0" ? (
                                                        <div className="col-span-12">
                                                            <div className="block text-center items-center font-semibold text-slate-500	">
                                                                Loading...
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            {displaySlots && displaySlots?.length > 0 ? (
                                                                <div className="inline-block py-2 flex flex-wrap gap-2">
                                                                    {displaySlots?.map((slot) => {
                                                                        let color = "";
                                                                        let percentage =
                                                                        (slot?.appointments?.length / slot?.book_per_slot) * 100;
                                                                        if (percentage === 100) {
                                                                            color = "rgb(255 126 119 / var(--tw-text-opacity))";
                                                                        }
                                                                        else if(percentage < 100 && percentage > 49){
                                                                            color = "rgb(198 144 53 / var(--tw-text-opacity))";
                                                                        }
                                                                        else if (percentage < 50 && percentage > 9){
                                                                            color = "rgb(144 238 144 / var(--tw-text-opacity))";
                                                                        }
                                                                        return (
                                                                            <>
                                                                                {slot.start_time < "12:00" && (
                                                                                    <button
                                                                                        type="button"
                                                                                        className={`min-w-[4.5rem] min-h-[2.25rem] sm:min-h-0 rounded-lg border border-gray-200 cursor-pointer
                                                                                            ${selectedSlot && selectedSlot === slot.start_time ? `bg-bgGreen` : `bg-white`} text-sm ${selectedSlot && selectedSlot === slot.start_time ? `text-white`
                                                                                            : `text-gray-700`} px-2 py-1.5 sm:py-1 hover:bg-bgGreen hover:text-white touch-manipulation`}
                                                                                        onClick={() => {
                                                                                            handleSelectedSlot(slot.start_time, percentage);
                                                                                        }}
                                                                                        style={{
                                                                                            background: !(selectedSlot === slot.start_time && percentage < 100) &&
                                                                                            `linear-gradient(90deg, ${color} ${percentage}%, #fff 0%)`,
                                                                                            color: percentage === 100 && "white",
                                                                                        }}
                                                                                        disabled={slot?.block}
                                                                                        title={slot?.block && "This slot is blocked"}
                                                                                    >
                                                                                        {slot.start_time} <sup>AM</sup>
                                                                                    </button>
                                                                                )}
                                                                            </>
                                                                        );
                                                                    })}
                                                                </div>
                                                            ) : (
                                                                <div className="col-span-12 h-40 flex justify-center items-center">
                                                                    <div className="block text-center items-center text-slate-500	">
                                                                        No slots
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                                <div className="md:col-span-6 min-w-0">
                                                    <div className="block text-center items-center">
                                                        <p className="text-c-black-600 font-semibold text-base">
                                                        PM
                                                        </p>
                                                    </div>
                                                    {isSlotsLoading && currentProviderValue?.id !== "0" ? (
                                                        <div className="col-span-12">
                                                            <div className="block text-center items-center font-semibold text-slate-500	">
                                                                Loading...
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <>
                                                        {displaySlots && displaySlots?.length > 0 ? (
                                                            <div className="inline-block py-2 flex flex-wrap gap-2">
                                                            {displaySlots?.map((slot) => {
                                                                let color = "";
                                                                let percentage = (slot?.appointments?.length / slot?.book_per_slot) * 100;
                                                                if(percentage === 100){
                                                                    color ="rgb(255 126 119 / var(--tw-text-opacity))";
                                                                }
                                                                else if (percentage < 100 && percentage > 49){
                                                                    color = "rgb(198 144 53 / var(--tw-text-opacity))";
                                                                }
                                                                else if(percentage < 50 && percentage > 9){
                                                                    color = "rgb(144 238 144 / var(--tw-text-opacity))";
                                                                }
                                                                return (
                                                                    <>
                                                                        {slot.start_time >= "12:00" && (
                                                                        <button
                                                                            type="button"
                                                                            className={`min-w-[4.5rem] min-h-[2.25rem] sm:min-h-0 rounded-lg border border-gray-200 cursor-pointer
                                                                            ${selectedSlot && selectedSlot === slot.start_time ? `bg-bgGreen` : `bg-white`} text-sm ${
                                                                            selectedSlot &&
                                                                            selectedSlot === slot.start_time
                                                                                ? `text-white`
                                                                                : `text-gray-700`
                                                                            } px-2 py-1.5 sm:py-1 hover:bg-bgGreen hover:text-white touch-manipulation`}
                                                                            onClick={() => {
                                                                                handleSelectedSlot(
                                                                                    slot.start_time,
                                                                                    percentage
                                                                                );
                                                                            }}
                                                                            style={{
                                                                                background:
                                                                                    !(
                                                                                    selectedSlot ===
                                                                                        slot.start_time &&
                                                                                    percentage < 100
                                                                                    ) &&
                                                                                    `linear-gradient(90deg, ${color} ${percentage}%, #fff 0%)`,
                                                                                color:
                                                                                    percentage === 100 && "white",
                                                                            }}
                                                                            disabled={slot?.block}
                                                                            title={
                                                                            slot?.block &&
                                                                            "This slot is blocked"
                                                                            }
                                                                        >
                                                                            {moment(`${slot.start_time}`, [
                                                                            "h:mm A",
                                                                            ]).format("hh:mm")}{" "} <sup>PM</sup>
                                                                        </button>
                                                                        )}
                                                                    </>
                                                                );
                                                            })}
                                                            </div>
                                                        ) : (
                                                            <div className="col-span-12 h-40 flex justify-center items-center">
                                                            <div className="block text-center items-center text-slate-500	">
                                                                No slots
                                                            </div>
                                                            </div>
                                                        )}
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {(authData?.is_provider && currentProviderValue && currentProviderValue?.id != 0) ?
                            <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-2 items-start sm:items-center pb-5 min-w-0">
                                <div className="shrink-0">
                                    <CheckBox
                                        title="Teleconsult"
                                        name="is_teleconsult" 
                                        control={control}
                                        disabled={isTeleconsultDisable}
                                    />
                                </div>
                                {(isTeleconsultValue && (vezoAllRooms || [])?.length) ?
                                    <>
                                        <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-2 w-full sm:w-auto">
                                            <div className="min-w-0">
                                                <ReactSelectField
                                                    title="Selected Room"
                                                    name="vezo_room"
                                                    control={control}
                                                    errors={errors}
                                                    rules={{
                                                        required: {
                                                            value: true,
                                                        },
                                                    }}
                                                    optionData={vezoAllRooms?.map((v, k) => ({
                                                        ...v,
                                                        label: v.room_name,
                                                    }))}
                                                    menuPlacement={"bottom"}
                                                />
                                            </div>
                                            <div className="min-w-0">
                                                <PatientContact formHandler={formHandler} contactToBeSelected={vezoSessionContacts} />
                                            </div>
                                        </div>
                                    </>
                                : null}
                                {!((vezoAllRooms || [])?.length) ? 
                                    <div className="text-sm break-words min-w-0">
                                        The selected provider does not have a vezo room. Please create a vezo room in EHR
                                    </div>
                                    :
                                null}
                            </div>
                        : null}
                        {isTeleconsultValue ? 
                            <ExternalGuest formHandler={formHandler} exteralGuests={exteralGuests} setExteralGuests={setExteralGuests} />
                            : null
                        }
                        
                        <div className="flex gap-2 items-center min-w-0">
                            <div className="relative w-full min-w-0">
                                <SpeechMic transformSpeech={onSpeechAvailable} />
                                <TextAreaField title="Comments" name={`comments`} control={control} />
                                <p className="text-c-black-600 font-normal text-sm uppercase break-words">
                                    This appointment is set to schedule at{" "}
                                    <span className="text-red-c-a">
                                        {" "}
                                        {moment(startDate).format("DD/MM/YYYY")}{" "}
                                        {selectedSlot && (
                                            <>{moment(selectedSlot, ["h:mm A"]).format("hh:mm")} </>
                                        )}
                                        {selectedSlot && <>{selectedSlot >= "12:00" ? "PM" : "AM"}</>}
                                    </span>
                                </p>
                            </div>
                        </div>
                    </div>
                </form>
            </PageDialogContent>
            <PageDialogFooter>
                    <BtnComponent text="Cancel" btnType="default" onClick={handleClosePopup} />
                    <BtnComponent text="Save" btnType='primary_theme' loading={isSavingAppts || isSavingProviderAppt} form="save_appointment_form" type="submit" />
            </PageDialogFooter>
        </>
    );
};
